import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
// oxlint-disable-next-line import/default
import StreamWorker from "Libs/stream.worker?worker";
import { isAgentReportTaskActivity } from "Reducers/activity/agentReport.selectors";

import type { Activity } from "Api/activity/types";

/**
 * How long the worker sits through silence before closing an agent run's log,
 * in poll passes of roughly 1.2 seconds — about twenty minutes, the task
 * container's own ceiling.
 *
 * The worker's default is 30 passes, near enough 37 seconds, which an agent run
 * exceeds on two ordinary paths: waiting on its task container, and waiting on
 * one long model call. At the default the worker closes mid-run and calls
 * `self.close()`, so everything the agent writes after that — the report line
 * included — is never read, and a run that succeeded shows an empty Report tab.
 *
 * Derived from the activity here rather than passed by callers: the log modal,
 * the expanded activity row and the run listener all stream these activities,
 * and a call site that forgets produces exactly that silent empty report.
 */
const AGENT_LOG_RETRY_ATTEMPTS = 1_000;

export const loadLogFromActivity = createAsyncThunk(
  "app/activity/stream",

  async (
    {
      activity,
      retryNumber = 0
    }: {
      activity: Activity;
      retryNumber?: number;
    },
    { dispatch }
  ) => {
    const worker = new StreamWorker();

    const platformLib = await import("Libs/platform");
    const client = platformLib.default;
    const accessToken = await client.getAccessToken();

    const { api_url } = client.getConfig();

    const absoluteActivityLogUrl = api_url + activity?._links?.log?.href;

    worker.postMessage({
      url: `${absoluteActivityLogUrl}?max_items=0&max_delay=1000`,
      accessToken,
      maxBatchNumber: 1000,
      maxRetryAttempts: isAgentReportTaskActivity(activity)
        ? AGENT_LOG_RETRY_ATTEMPTS
        : undefined
    });
    worker.onmessage = async e => {
      const { data } = e;
      if (!data.success) {
        if (
          retryNumber < 3 &&
          (data.error === 401 || data.error === "Failed to fetch")
        ) {
          // Re-authenticate
          await client.reAuthenticate();

          // Retry
          dispatch(
            loadLogFromActivity({
              activity,
              retryNumber: ++retryNumber
            })
          );
          return;
        }

        logger(
          {
            errorMessage: data.error,
            activityId: activity.id
          },
          {
            action: "loadLogFromActivity"
          }
        );
        return;
      }

      // streamFinished is defined in the slice below; mutually recursive with loadLogFromActivity.
      dispatch(
        // eslint-disable-next-line no-use-before-define
        streamFinished({
          activityId: activity.id,
          stream: data.fragmentSet,
          done: data.done
        })
      );
    };
  }
);

export type Log = {
  _id?: number;
  seal: boolean;
  data: {
    message: string;
    timestamp: Date | string | number;
  };
};

export const LOG_STATUS = Object.freeze({
  PENDING: "pending",
  FULFILLED: "fulfilled",
  REJECTED: "rejected"
} as const);

export type LogStatus = (typeof LOG_STATUS)[keyof typeof LOG_STATUS];

type LogState = {
  [activityId: string]:
    | {
        loading?: boolean;
        status?: LogStatus;
        log?: Log[];
        streamEnded?: boolean;
        errors?: unknown;
      }
    | undefined;
};

const initialState: LogState = {};

const logs = createSlice({
  name: "logs",
  initialState,
  reducers: {
    streamFinished(state, { payload }) {
      const { activityId, done, stream } = payload;
      setDeep(state, [activityId, "loading"], false);
      setDeep(state, [activityId, "status"], LOG_STATUS.FULFILLED);
      setDeep(state, [activityId, "streamEnded"], done);
      setDeep(state, [activityId, "log"], stream);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadLogFromActivity.pending, (state, action) => {
        const { activity } = action.meta.arg;
        setDeep(state, [activity.id, "status"], LOG_STATUS.PENDING);
        setDeep(state, [activity.id, "loading"], true);
      })
      .addCase(loadLogFromActivity.rejected, (state, action) => {
        const { payload, meta } = action;
        const activity = meta.arg.activity;
        setDeep(state, [activity.id, "status"], LOG_STATUS.REJECTED);
        setDeep(state, [activity.id, "loading"], false);
        setDeep(state, [activity.id, "errors"], payload);
      });
  }
});

export const { streamFinished } = logs.actions;
export default logs.reducer;
